import { Dialog } from "@headlessui/react"
import { KButton, KInput, KSpan, KTitleSpan } from "kahuna-base-react-components"
import React, { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { discoveryMode } from "../../../../apis"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../constants"
import { lang } from "../../../../constants/languages"
import { DiscoveryModeCampaign, DiscoveryModeTrack } from "../../../../types"
import { formatIsoDate, mapListToCamelCase, mapToCamelCase } from "../../../../utility"

const PAGE_SIZE = 50

type DiscoveryModeModalProps = {
  open: boolean
  campaign?: DiscoveryModeCampaign
  onClose: () => void
  onSubmitted: (campaign: DiscoveryModeCampaign) => void
}

const DiscoveryModeModal: FC<DiscoveryModeModalProps> = ({ open, campaign, onClose, onSubmitted }) => {
  const [tracks, setTracks] = useState<DiscoveryModeTrack[]>([])
  const [count, setCount] = useState(0)
  const [selectedTrackIds, setSelectedTrackIds] = useState<number[]>([])
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const fetchTracks = async (offset: number, searchText: string) => {
    const searchParam = searchText ? `&search=${encodeURIComponent(searchText)}` : ""
    const response = await discoveryMode(`/eligible_tracks/?limit=${PAGE_SIZE}&offset=${offset}${searchParam}`, "get")

    setCount(response.data.count)
    const fetchedTracks = mapListToCamelCase<DiscoveryModeTrack>(response.data.results)
    setTracks((previousTracks) => (offset === 0 ? fetchedTracks : [...previousTracks, ...fetchedTracks]))
  }

  // The modal keeps its state between openings, so reset the search on every open.
  useEffect(() => {
    if (!open) return

    setSearch("")
    setDebouncedSearch("")
  }, [open])

  useEffect(() => {
    if (!open) return

    setSelectedTrackIds(campaign?.selectedTrackIds || [])
  }, [open, campaign])

  useEffect(() => {
    if (!open) return

    const timeoutId = setTimeout(() => setDebouncedSearch(search), 500)
    return () => clearTimeout(timeoutId)
  }, [open, search])

  useEffect(() => {
    if (!open) return

    setLoading(true)
    fetchTracks(0, debouncedSearch)
      .catch(() => toast.error(lang.catalog.discovery_mode.fetch_error, errorFilledToastOptions))
      .finally(() => setLoading(false))
  }, [open, debouncedSearch])

  const onClickLoadMore = () => {
    setLoadingMore(true)
    fetchTracks(tracks.length, debouncedSearch)
      .catch(() => toast.error(lang.catalog.discovery_mode.fetch_error, errorFilledToastOptions))
      .finally(() => setLoadingMore(false))
  }

  const toggleTrack = (trackId: number) => {
    setSelectedTrackIds((previousIds) =>
      previousIds.includes(trackId) ? previousIds.filter((id) => id !== trackId) : [...previousIds, trackId]
    )
  }

  const onClickSelectAll = () => {
    setSelectedTrackIds((previousIds) => Array.from(new Set([...previousIds, ...tracks.map((track) => track.id)])))
  }

  const onSubmit = () => {
    setSubmitting(true)
    discoveryMode("/submit/", "post", { track_ids: selectedTrackIds })
      .then((response) => {
        toast.success(lang.catalog.discovery_mode.success, successFilledToastOptions)
        onSubmitted(mapToCamelCase<DiscoveryModeCampaign>(response.data))
        onClose()
      })
      .catch(() => toast.error(lang.catalog.discovery_mode.error, errorFilledToastOptions))
      .finally(() => setSubmitting(false))
  }

  const renderDeadlineInfo = () => {
    if (!campaign) return null

    return (
      <div className="flex flex-row gap-2 p-3 rounded-[10px] bg-[#F7F7F7]">
        <img src="/catalog_icons/information-line.svg" alt="info" className="w-4 h-4 mt-0.5" />
        <KSpan
          text={lang.catalog.discovery_mode.deadline_info
            .replace("<deadline>", formatIsoDate(campaign.deadlineDate))
            .replace("<campaign>", campaign.campaignLabel)}
          fontSize={12}
          lineHeight="16px"
          color="#111"
        />
      </div>
    )
  }

  const renderTrackRow = (track: DiscoveryModeTrack) => {
    const selected = selectedTrackIds.includes(track.id)

    return (
      <div
        key={track.id}
        data-testid={`discovery-mode-track-${track.id}`}
        className="flex flex-row gap-3 items-center px-3 py-2 rounded-[10px] cursor-pointer hover:bg-[#F7F7F7]"
        style={{ background: selected ? "#F7F7F7" : "transparent" }}
        onClick={() => toggleTrack(track.id)}
      >
        {/* The row itself toggles the track, so the checkbox must not toggle it a second time. */}
        <div onClick={(event) => event.stopPropagation()}>
          <input
            value={String(track.id)}
            onChange={() => toggleTrack(track.id)}
            type="checkbox"
            checked={selected}
            width={16}
            height={16}
            style={{ accentColor: "black" }}
          />
        </div>
        <div className="h-[36px] w-[36px] rounded-[8px] bg-[#F3F3F3] overflow-hidden shrink-0">
          {track.coverFileUrl && <img src={track.coverFileUrl} alt={track.title} className="w-full h-full" />}
        </div>
        <div className="flex flex-col grow overflow-hidden">
          <KSpan text={track.title} color="#111" fontWeight={500} />
          <KSpan text={track.artists || track.releaseTitle} fontSize={12} lineHeight="16px" color="#6B7280" />
        </div>
        <div className="flex flex-col items-end shrink-0">
          <KSpan text={track.isrc} fontSize={12} lineHeight="16px" color="#6B7280" />
          <KSpan
            text={track.releaseDate ? formatIsoDate(track.releaseDate) : ""}
            fontSize={12}
            lineHeight="16px"
            color="#6B7280"
          />
        </div>
      </div>
    )
  }

  const renderTrackList = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-[240px]">
          <KSpan text="..." color="#6B7280" />
        </div>
      )
    }

    if (tracks.length === 0) {
      return (
        <div className="flex items-center justify-center h-[240px] text-center px-6">
          <KSpan
            text={debouncedSearch ? lang.catalog.discovery_mode.empty_search : lang.catalog.discovery_mode.empty_list}
            color="#6B7280"
          />
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-1 overflow-y-auto" style={{ maxHeight: "42vh" }}>
        {tracks.map(renderTrackRow)}
        {tracks.length < count && (
          <div className="flex justify-center py-2">
            <KButton
              dataTestId="discovery-mode-load-more-button"
              text={lang.catalog.discovery_mode.load_more.replace("<param>", `${tracks.length}/${count}`)}
              background="#F7F7F7"
              hoverBackground="#F3F3F3"
              width="auto"
              height="auto"
              padding="8px 14px"
              disabled={loadingMore}
              onClick={onClickLoadMore}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <div className="fixed w-screen h-screen top-0 left-0 z-250 flex items-center justify-center bg-[#0000004d]">
        <div className="p-6 shadow-md w-[620px] rounded-[10px] bg-[#fff]" style={{ maxHeight: "90vh" }}>
          <div className="flex flex-col gap-5">
            <div className="flex flex-row justify-between items-start">
              <div className="flex flex-col gap-1">
                <KTitleSpan text={lang.catalog.discovery_mode.title} fontSize={20} lineHeight="28px" />
                <KSpan
                  text={
                    campaign
                      ? lang.catalog.discovery_mode.campaign_subtitle.replace("<campaign>", campaign.campaignLabel)
                      : lang.catalog.discovery_mode.description
                  }
                  fontSize={12}
                  lineHeight="16px"
                  color="#6B7280"
                />
              </div>
              <KButton
                dataTestId="discovery-mode-close-button"
                icon="/admin_icons/close.svg"
                onClick={onClose}
                background="transparent"
                shadowDisabled
                width="32px"
                height="32px"
                padding="6px"
              />
            </div>

            {renderDeadlineInfo()}

            <KInput
              value={search}
              onChange={setSearch}
              leftIcon="/catalog_icons/search-grey.svg"
              background="#F7F7F7"
              shadowDisabled
              placeholder={lang.catalog.discovery_mode.search_placeholder}
            />

            {renderTrackList()}

            <div className="flex flex-row justify-between items-center">
              <div className="flex flex-row gap-3 items-center">
                <KSpan
                  text={lang.catalog.discovery_mode.selected_count.replace(
                    "<param>",
                    selectedTrackIds.length.toString()
                  )}
                  color="#111"
                  fontWeight={500}
                />
                {tracks.length > 0 && (
                  <KButton
                    dataTestId="discovery-mode-select-all-button"
                    text={lang.catalog.discovery_mode.select_all}
                    onClick={onClickSelectAll}
                    background="transparent"
                    shadowDisabled
                    width="auto"
                    height="auto"
                    padding="0px"
                    fontSize={12}
                    textColor="#6B7280"
                  />
                )}
                {selectedTrackIds.length > 0 && (
                  <KButton
                    dataTestId="discovery-mode-clear-button"
                    text={lang.catalog.discovery_mode.clear_selection}
                    onClick={() => setSelectedTrackIds([])}
                    background="transparent"
                    shadowDisabled
                    width="auto"
                    height="auto"
                    padding="0px"
                    fontSize={12}
                    textColor="#6B7280"
                  />
                )}
              </div>
              <div className="flex flex-row gap-3">
                <KButton
                  dataTestId="discovery-mode-cancel-button"
                  width="120px"
                  text={lang.catalog.discovery_mode.cancel_button}
                  background="#fff"
                  onClick={onClose}
                />
                <KButton
                  dataTestId="discovery-mode-submit-button"
                  width="120px"
                  text={lang.catalog.discovery_mode.submit_button}
                  background="#000"
                  textColor="#fff"
                  disabled={submitting || loading}
                  onClick={onSubmit}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default DiscoveryModeModal
